// src/App.js
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
// ... (بقية الاستيرادات تبقى كما هي)
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import CssBaseline from '@mui/material/CssBaseline';
import { AnimatePresence } from 'framer-motion';

// استيراد الصفحات
import LoginPage from './components/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import ClientDashboardPage from './components/ClientDashboardPage';
import ConversationListPage from './components/ConversationListPage';
import ConversationDetailPage from './components/ConversationDetailPage';
import KnowledgeBasePage from './components/KnowledgeBasePage';
import ConnectionTestPage from './components/ConnectionTestPage';
import ConnectionTestDetailPage from './components/ConnectionTestDetailPage';
import SettingsPage from './components/SettingsPage';
import TemplatesPage from './components/TemplatesPage';
import ReportsPage from './components/ReportsPage';
import BulkMessagingPage from './components/BulkMessagingPage';
import CampaignsPage from './components/CampaignsPage';
import LearningSuggestionsPage from './components/LearningSuggestionsPage';
import AppointmentsPage from './components/AppointmentsPage';
import BotPlaygroundPage from './components/BotPlaygroundPage';

// Admin Pages
import AdminDashboard from './components/admin/AdminDashboard';
import AdminClientListPage from './components/admin/AdminClientListPage';
import AdminClientDetailPage from './components/admin/AdminClientDetailPage';
import AdminClientEditPage from './components/admin/AdminClientEditPage';
import AdminClientNewPage from './components/admin/AdminClientNewPage';
import AdminChartsPage from './components/admin/AdminChartsPage';
import AdminSystemLogsPage from './components/admin/AdminSystemLogsPage';
import AdminDepartmentsPage from './components/admin/AdminDepartmentsPage';
import AdminTransferStatsPage from './components/admin/AdminTransferStatsPage';

// ... (إعدادات الثيم و RTL تبقى كما هي)
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

// ===== نظام التصميم الموحّد (نفس لغة لوحة التحكم) =====
const DS = {
  ink: '#0f172a',
  inkSoft: '#334155',
  muted: '#64748b',
  border: '#e9eef5',
  canvas: '#f4f7fb',
  brand: '#0ea5e9',
  brandDark: '#0284c7',
  brandSoft: '#e6f6fe',
  shadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
};

const lightTheme = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'light',
    primary: { main: DS.brand, dark: DS.brandDark, contrastText: '#ffffff' },
    secondary: { main: '#1e293b' },
    background: { default: DS.canvas, paper: '#ffffff' },
    text: { primary: DS.ink, secondary: DS.muted },
    divider: DS.border,
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: 'Cairo, Roboto, Arial, sans-serif',
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: DS.canvas },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 18 },
        elevation1: { boxShadow: DS.shadow },
        elevation2: { boxShadow: DS.shadow },
        elevation3: { boxShadow: DS.shadow },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: `1px solid ${DS.border}`,
          boxShadow: DS.shadow,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, boxShadow: 'none' },
        containedPrimary: {
          '&:hover': { boxShadow: '0 6px 16px rgba(14,165,233,0.35)' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: DS.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bfdbfe' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700, borderRadius: 8 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f8fafc',
            color: DS.inkSoft,
            fontWeight: 700,
            borderBottom: `1px solid ${DS.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${DS.border}` },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&.Mui-selected': {
            backgroundColor: DS.brandSoft,
            color: DS.brandDark,
            '& .MuiListItemIcon-root': { color: DS.brand },
            '&:hover': { backgroundColor: DS.brandSoft },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 18 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: 8, fontWeight: 600 },
      },
    },
  },
});

function PrivateRoute({ children }) {
  const isAuthenticated = !!localStorage.getItem('accessToken');
  return isAuthenticated ? children : <Navigate to="/login" />;
}

import { WebSocketProvider } from './contexts/WebSocketContext';

// ... (previous imports)

function App() {
  const location = useLocation();

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <WebSocketProvider>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/login" element={<LoginPage />} />

              <Route
                path="/*"
                element={<PrivateRoute><DashboardLayout /></PrivateRoute>}
              >
                {/* === روابط العميل === */}
                <Route path="dashboard" element={<ClientDashboardPage />} />
                <Route path="conversations" element={<ConversationListPage />} />
                <Route path="conversations/:id" element={<ConversationDetailPage />} />
                <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                <Route path="test-connection" element={<ConnectionTestPage />} />
                <Route path="connection-test-details" element={<ConnectionTestDetailPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="appointments" element={<AppointmentsPage />} />
                <Route path="playground" element={<BotPlaygroundPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="bulk-messaging" element={<BulkMessagingPage />} />
                <Route path="learning-center" element={<LearningSuggestionsPage />} />

                {/* === روابط الأدمن === */}
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/dashboard" element={<AdminDashboard />} />
                <Route path="admin/clients" element={<AdminClientListPage />} />
                <Route path="admin/clients/new" element={<AdminClientNewPage />} />
                <Route path="admin/clients/:id" element={<AdminClientDetailPage />} />
                <Route path="admin/clients/edit/:id" element={<AdminClientEditPage />} />
                <Route path="admin/charts" element={<AdminChartsPage />} />
                <Route path="admin/logs" element={<AdminSystemLogsPage />} />
                <Route path="admin/departments" element={<AdminDepartmentsPage />} />
                <Route path="admin/transfer-stats" element={<AdminTransferStatsPage />} />

                {/* ✨ UPDATED: تحديث الرابط الرئيسي الموجه */}
                <Route
                  index
                  element={
                    <Navigate to={
                      localStorage.getItem('isAdmin') === 'true'
                        ? "/admin/dashboard"
                        : "/dashboard"
                    } />
                  }
                />
              </Route>
            </Routes>
          </AnimatePresence>
        </WebSocketProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
